import { useState, useCallback, useEffect, useRef } from 'react';
import { Timer } from 'lucide-react';
import { searchSectors } from '../../services/api';
import { LAP_COLORS } from '../LapSelector/LapSelector';
import styles from './SearchPage.module.css';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

/**
 * Sector search results — one "top N" table per defined sector. The query runs
 * against the current sector boundaries (saved or not); results go stale when
 * the boundaries change, so a re-run is prompted instead of auto-firing on
 * every divider drag.
 */
export default function SectorResultsPanel({
  trackId, scope, vehicleId, sectors, onLoadSession, initialState, onStateChange,
}) {
  const [results, setResults] = useState(null);   // [{sector, results:[…]}]
  const [dedupe, setDedupe] = useState(initialState?.dedupe ?? false);
  const [loading, setLoading] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(null);
  // Whether a search has completed since the last invalidation — reported up
  // so a Back-navigation/deep-link restore knows to auto re-run it (below).
  const [ran, setRan] = useState(false);

  // New track/scope/car/boundaries invalidate what's on screen — except the
  // very first time, when a restore may still need its auto-run (below) to see
  // a `ran: true` it hasn't consumed yet.
  const firstInvalidateRef = useRef(true);
  useEffect(() => {
    if (firstInvalidateRef.current) firstInvalidateRef.current = false;
    else setRan(false);
    setResults(null); setStale(false); setError(null);
  }, [trackId, scope, vehicleId]);
  useEffect(() => { if (results) setStale(true); }, [sectors, dedupe]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async () => {
    if (!trackId || !sectors.length) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchSectors({
        track_id: trackId, scope, sectors, vehicle_id: vehicleId,
        top_n: 5, dedupe: dedupe ? 'session' : 'none',
      });
      setResults(res.sectors || []);
      setStale(false);
      setRan(true);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [trackId, scope, vehicleId, sectors, dedupe]);

  // Auto re-run once, only when restoring a page where a search had already
  // completed (initialState.ran) — waits for sectors to actually be loaded.
  const autoRunRef = useRef(!!initialState?.ran);
  useEffect(() => {
    if (!autoRunRef.current || !trackId || !sectors.length) return;
    autoRunRef.current = false;
    run();
  }, [trackId, sectors, run]);

  useEffect(() => { onStateChange?.({ dedupe, ran }); }, [dedupe, ran, onStateChange]);

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <span><Timer size={14} /> Best sector times</span>
        <span className={styles.headActions}>
          <label className={styles.checkCtl} title="Keep only each session's best lap per sector">
            <input type="checkbox" checked={dedupe} onChange={e => setDedupe(e.target.checked)} />
            Best per session
          </label>
          {stale && <span className={styles.dirtyBadge}>sectors changed — re-run</span>}
          <button className={styles.runBtn} onClick={run} disabled={loading || !sectors.length}>
            {loading ? 'Searching…' : 'Run sector search'}
          </button>
        </span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!results && !error && (
        <div className={styles.empty}>
          Run the search to rank the top 5 sector times across every lap in scope.
        </div>
      )}

      {results && (
        <div className={styles.sectorGrid}>
          {results.map(({ sector, results: rows }) => {
            // Same color TrackMap's Sector mode and the speed trace's sector
            // bands use, so all three views read as one consistent color key.
            const idx = sectors.findIndex(s => s.id === sector.id);
            const color = LAP_COLORS[(idx < 0 ? 0 : idx) % LAP_COLORS.length];
            return (
            <div key={sector.id} className={styles.sectorBlock}>
              <div className={styles.sectorName} style={{ boxShadow: `inset 3px 0 0 ${color}` }}>
                {sector.label}
                <span className={styles.muted}>
                  {' '}{Math.round(sector.start_frac * 100)}–{Math.round(sector.end_frac * 100)}%
                </span>
              </div>
              {rows.length === 0 ? (
                <div className={styles.empty}>No laps.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>#</th><th>Driver</th><th>Date</th>
                      <th>Sector</th><th>Lap</th><th>Vehicle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.session_id}-${r.lap_number}`}
                        className={`${styles.linkRow} ${i === 0 ? styles.bestRow : ''}`}
                        title="Open this session in the analysis view"
                        onClick={() => onLoadSession?.(r.session_id)}>
                        <td className={styles.muted}>{i + 1}</td>
                        <td>{r.user_display_name || '—'}</td>
                        <td>{fmtDate(r.event_date)}</td>
                        <td><strong>{r.sector_time_s?.toFixed(3)}s</strong></td>
                        <td title={`Lap ${r.lap_number}`}>{r.lap_time_str}</td>
                        <td>{r.vehicle_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
